<script setup lang="ts">
import type { CartItemId, SimpleCartItem } from "~/stores/CartStore";
import { KsIcon, KsInput } from "@aschehoug/kloss";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{ item: SimpleCartItem }>();

const emit = defineEmits<{
  remove: [id: CartItemId];
  updateQuantity: [id: UUID, amount: number];
}>();

const totItems = ref(props.item.amount);
const accessId = useId();

const min = computed(() =>
  Math.min(props.item.fixedAmount ? props.item.amount : 1, 1),
);

const toast = inject(keys.toast, null);

const isShowingToast = ref(false);

const auth = useAuthorizeStore();

function handleAccess(event: Event) {
  const newValue = Number((event.target as HTMLInputElement).value);
  if (props.item.id && isValidUuid(props.item.id)) {
    if (newValue >= min.value) {
      totItems.value = newValue;
    } else {
      totItems.value = min.value;
      (event.target as HTMLInputElement).value = min.value.toString();
      if (!isShowingToast.value) {
        isShowingToast.value = true;
        toast?.info({
          message: `Det er vanskelig å selge færre tilganger enn ${min.value}, endre antallet om du vil kjøpe. Ønsket du egentlig å slette varen fra handlekurven?`,
          persistent: true,
          buttons: [
            {
              text: "Ja",
              onClick() {
                emit("remove", props.item.id);
              },
            },
            {
              text: "Nei",
              onClick() {},
            },
          ],
          onClose() {
            isShowingToast.value = false;
          },
        });
      }
    }
    emit("updateQuantity", props.item.id, totItems.value);
  }
}

const originalPrice = computed(
  () => (props.item.price?.original.incVat ?? 0) * totItems.value,
);

const totPrice = computed(
  () => (props.item.price?.actual.incVat ?? 0) * totItems.value,
);
</script>

<template>
  <li class="list__item" data-dd-privacy="hidden">
    <figure class="item__cover">
      <CrystallizeImage
        :image="item.cover"
        :alt="`Coverbilde av ${item.name}`"
        img-class="size-full object-cover"
      />
      <UiGraphicsLabelBasket :label="item.type" class="cover__label" />
    </figure>
    <div class="item__description">
      <span class="description__type" v-text="item.type" />
      <span
        v-if="item.parentTitle"
        class="mt-2 text-xs"
        v-text="item.parentTitle"
      />
      <h3 v-text="item.name" />
      <p class="description__id">
        ISBN: {{ item.isbn }}
      </p>
      <div
        v-if="item.description"
        class="description__text"
        v-html="item.description"
      />
      <p class="description__unit-price">
        <s
          v-if="item.price?.discounted"
          v-text="norwegianPrice(item.price.original.incVat)"
        />
        {{ norwegianPrice(item.price?.actual.incVat) }} pr. tilgang (inkl. mva.)
      </p>
      <p v-if="auth.isConsumer" class="text-sm text-gray-40">
        1-årslisens, avsluttes automatisk
      </p>
      <template v-else>
        <p
          v-if="item.price?.actual.isMetered"
          class="description__warning--tiers"
        >
          Din skole har kvantumsrabatt hos Aschehoug, din avtalepris er
          {{ norwegianPrice(item.price.actual.exVat, false) }} (ekskl. mva.) pr.
          elev
        </p>
        <label
          :for="accessId"
          :aria-label="
            item.fixedAmount
              ? item.isTrial
                ? `${item.amount} prøvelisens. Verdi kan ikke endres!`
                : `${item.amount} er totalt antall elever registrert på skolen din. Verdi kan ikke endres!`
              : `${item.amount} tilganger`
          "
        >
          Tilganger
        </label>
        <KsInput
          :id="accessId"
          :value="item.amount"
          type="number"
          class="description__quantity"
          :disabled="item.fixedAmount"
          :aria-hidden="item.fixedAmount"
          :min="min"
          @change="handleAccess"
          @focus="$event.target.select()"
        />
        <span
          v-if="item.fixedAmount && !item.isTrial"
          class="description__warning--tot"
          aria-hidden="true"
        >
          Elevantall registrert på din skole. Ta
          <NuxtLink to="/kundeservice">kontakt med oss</NuxtLink> dersom
          antallet er feil.
        </span>
      </template>
      <button
        class="description__delete"
        :aria-label="`fjern ${item.name} fra handlekurv`"
        @click="emit('remove', item.id)"
      >
        <KsIcon :icon="faTrash" :scale="1.2" /> Fjern
      </button>
    </div>
    <div class="item__total">
      <s
        v-if="item.price?.discounted"
        :aria-label="`opprinnelig pris ${originalPrice}`"
        v-text="norwegianPrice(originalPrice, false)"
      />
      <p :aria-label="`nåværende pris ${totPrice}`">
        <span v-if="item.price?.discounted" class="total__warning">
          Kampanjepris
        </span>
        {{ norwegianPrice(totPrice, false) }}
      </p>
    </div>
  </li>
</template>

<style scoped lang="scss">
.list__item {
  border-bottom: 1px solid theme("colors.beige.30");
  padding: 2rem 0 1rem 0;
  display: flex;
  flex-flow: row wrap;
  gap: 2rem;

  .item__description {
    position: relative;
    font-size: pixelsToRem(15);
    display: flex;
    flex-flow: column nowrap;
    max-width: 50%;

    label {
      margin: 1rem auto 0.5rem 0;
    }

    .description__quantity {
      text-align: center;
      max-width: 5rem;
      padding: 0.6rem;
      border: 0.14rem solid theme("colors.beige.30");

      &[disabled] {
        background: lightgrey;
      }
    }

    .description__warning--tot {
      margin: 0.5rem 0 0 0;
      color: theme("colors.red.30");

      > a {
        color: inherit;
      }
    }

    .description__warning--tiers {
      margin: -0.5rem 0 0 0;
      color: theme("colors.red.30");
    }

    .description__id {
      margin-top: 1rem;
      font-size: pixelsToRem(14);
    }

    .description__type {
      font-weight: 300;
      font-size: 0.9rem;
    }

    .description__text {
      margin-top: 1rem;
      font-size: pixelsToRem(14);
    }

    .description__unit-price {
      margin: 0.8rem 0;

      s {
        color: theme("colors.red.30");
        font-weight: 400;
      }
    }

    .description__delete {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 0.6rem;
      margin: 2rem 0;
      text-align: left;
      cursor: pointer;
    }
  }

  .item__cover {
    position: relative;
    width: pixelsToRem(130);
    height: pixelsToRem(185);
    background-color: #006a52;

    img {
      width: 100%;
      height: 100%;
    }

    .cover__label {
      position: absolute;
      width: auto;
      top: 70%;
      left: -0.94rem;
      z-index: 1;
    }
  }

  .item__total {
    display: flex;
    flex-flow: column nowrap;
    padding: 2rem 0 1rem 0;
    align-items: flex-end;
    justify-content: flex-end;
    font-weight: bold;
    flex: 1;
    text-align: right;

    .total__warning {
      font-size: 0.8rem;
      text-align: right;
    }

    p span {
      color: theme("colors.red.30");
      font-weight: 300;
    }
  }
}
</style>
